/**
 * useLocation - Real-time location tracking hook for Par Seeker
 * 
 * Uses browser Geolocation API to track user's position with high accuracy.
 * Sends location updates to backend for:
 * 1. Calculating real distances to other golfers in PS Golfers directory
 * 2. Future GPS features like distance-to-pin on the golf course
 * 
 * Privacy: Location is only tracked while the app is active.
 * User must grant permission when prompted.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Update location every 30 seconds when position changes
const UPDATE_INTERVAL = 30000;
// Minimum distance change (in meters) to trigger an update
const MIN_DISTANCE_CHANGE = 50;

export function useLocation() {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'granted', 'denied', 'prompt'
  const [isTracking, setIsTracking] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const watchIdRef = useRef(null);
  const lastSentRef = useRef(null);
  const updateTimeoutRef = useRef(null);

  // Check if geolocation is supported
  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Calculate distance between two points (for checking if position changed significantly)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Send location to backend
  const sendLocationToBackend = useCallback(async (position) => {
    const { latitude, longitude, accuracy, altitude, heading, speed } = position.coords;
    
    // Check if we should send update (moved enough or enough time passed)
    if (lastSentRef.current) {
      const distance = getDistance(
        lastSentRef.current.latitude,
        lastSentRef.current.longitude,
        latitude,
        longitude
      );
      const timeSinceLastUpdate = Date.now() - lastSentRef.current.timestamp;
      
      // Skip if position hasn't changed much and not enough time passed
      if (distance < MIN_DISTANCE_CHANGE && timeSinceLastUpdate < UPDATE_INTERVAL) {
        return;
      }
    }

    try {
      await axios.post(`${API}/api/location/update`, {
        latitude,
        longitude,
        accuracy,
        altitude,
        heading,
        speed
      });

      lastSentRef.current = {
        latitude,
        longitude,
        timestamp: Date.now()
      };
      setLastUpdate(new Date());
      console.log(`📍 Location updated: ${latitude.toFixed(6)}, ${longitude.toFixed(6)} (±${accuracy?.toFixed(0)}m)`);
    } catch (err) {
      console.error('Failed to send location to backend:', err);
    }
  }, []);

  // Handle position update
  const handlePositionUpdate = useCallback((position) => {
    const { latitude, longitude, accuracy, altitude, heading, speed } = position.coords;
    
    setLocation({
      latitude,
      longitude,
      accuracy,
      altitude,
      heading,
      speed,
      timestamp: position.timestamp
    });
    setError(null);

    // Send to backend
    sendLocationToBackend(position);
  }, [sendLocationToBackend]);

  // Handle position error
  const handlePositionError = useCallback((err) => {
    console.error('Geolocation error:', err);
    
    let errorMessage;
    switch (err.code) {
      case err.PERMISSION_DENIED:
        errorMessage = 'Location permission denied. Enable location in your browser settings.';
        setPermissionStatus('denied');
        break;
      case err.POSITION_UNAVAILABLE:
        errorMessage = 'Location unavailable. Make sure GPS is enabled.';
        break;
      case err.TIMEOUT:
        errorMessage = 'Location request timed out. Please try again.';
        break;
      default:
        errorMessage = 'Unable to get location.';
    }
    
    setError(errorMessage);
  }, []);

  // Start tracking location
  const startTracking = useCallback(() => {
    if (!isSupported) {
      setError('Geolocation is not supported by your browser.');
      return false;
    }

    if (watchIdRef.current !== null) {
      // Already tracking
      return true;
    }

    setIsTracking(true);
    setError(null);

    // High accuracy options for GPS
    const options = {
      enableHighAccuracy: true, // Use GPS if available
      timeout: 10000,           // 10 second timeout
      maximumAge: 0             // Don't use cached position
    };

    // Watch position continuously
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      handlePositionError,
      options
    );

    // Also get immediate position
    navigator.geolocation.getCurrentPosition(
      handlePositionUpdate,
      handlePositionError,
      options
    );

    console.log('📍 Location tracking started');
    return true;
  }, [isSupported, handlePositionUpdate, handlePositionError]);

  // Stop tracking location
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setIsTracking(false);
      console.log('📍 Location tracking stopped');
    }
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }
  }, []);

  // Request permission and start tracking
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      setError('Geolocation is not supported by your browser.');
      return false;
    }

    // Check if permission API is available
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(result.state);
        
        result.addEventListener('change', () => {
          setPermissionStatus(result.state);
          if (result.state === 'granted' && !isTracking) {
            startTracking();
          } else if (result.state === 'denied') {
            stopTracking();
          }
        });

        if (result.state === 'granted') {
          return startTracking();
        } else if (result.state === 'prompt') {
          // Will trigger the browser's permission dialog
          return startTracking();
        } else {
          setError('Location permission denied. Please enable it in your browser settings.');
          return false;
        }
      } catch {
        // Permissions API not fully supported, try anyway
        return startTracking();
      }
    } else {
      // No permissions API, just try to start
      return startTracking();
    }
  }, [isSupported, isTracking, startTracking, stopTracking]);

  // Get current position once (without tracking)
  const getCurrentPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!isSupported) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          handlePositionUpdate(position);
          resolve(position);
        },
        (err) => {
          handlePositionError(err);
          reject(err);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }, [isSupported, handlePositionUpdate, handlePositionError]);

  // Calculate distance to another point in miles
  const distanceTo = useCallback((lat, lng) => {
    if (!location) return null;
    
    const R = 3959; // Earth's radius in miles
    const φ1 = (location.latitude * Math.PI) / 180;
    const φ2 = (lat * Math.PI) / 180;
    const Δφ = ((lat - location.latitude) * Math.PI) / 180;
    const Δλ = ((lng - location.longitude) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }, [location]);

  // Calculate distance to another point in yards (for golf)
  const distanceToYards = useCallback((lat, lng) => {
    const miles = distanceTo(lat, lng);
    return miles ? miles * 1760 : null;
  }, [distanceTo]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  // Check permission status on mount
  useEffect(() => {
    if (isSupported && 'permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => setPermissionStatus(result.state))
        .catch(() => {});
    }
  }, [isSupported]);

  return {
    // Current location data
    location,
    error,
    permissionStatus,
    isTracking,
    lastUpdate,
    isSupported,
    
    // Actions
    requestPermission,
    startTracking,
    stopTracking,
    getCurrentPosition,
    
    // Distance calculations
    distanceTo,
    distanceToYards,
    
    // Convenience getters
    latitude: location?.latitude,
    longitude: location?.longitude,
    accuracy: location?.accuracy,
    hasLocation: location !== null
  };
}

export default useLocation;
